/**
 * Expiry status for a Stock Levels row (Rishabh 2026-10-12: "it should be very
 * clear at a glance which SKUs are already expired, expiring soon, not close").
 *
 * Inputs are what the page already has:
 *  - item: a stock-levels row (primary_sku, child_skus, warehouses[{warehouse_code, quantity}])
 *  - batches: bulk-batches rows for the SKU (expiry_date, has_expiry, is_expired, current_quantity)
 *  - warehouseExpiry: GET /api/stock-levels/warehouse-expiry → { holds, lots }
 *
 * Blocked (not sellable) per warehouse = the larger of what the warehouse itself
 * reports as expired / on hold and SCM's own expired batch units, capped at that
 * warehouse's on-hand. The larger, not the sum: both describe the same physical
 * units, so adding them would block stock twice.
 */

// ~9 months: the same horizon as the "expiring within 9 months" sheet.
export const EXPIRING_SOON_DAYS = 273;

export const HOLD_SOURCE_LABEL = {
  warehouse_expired_bin: 'in warehouse Expired bin',
  warehouse_expired_lot: 'expired lot at warehouse',
  warehouse_on_hold: 'on hold at warehouse',
  scm_batch: 'past batch expiry date',
};

const DAY_MS = 24 * 60 * 60 * 1000;

const toUtcDay = (value) => {
  const s = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const t = Date.UTC(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));
  const year = Number(s.slice(0, 4));
  if (year <= 1971 || year >= 2090) return null; // placeholders, not real dates
  return t;
};

export function daysUntil(dateStr, today = new Date()) {
  const t = toUtcDay(dateStr);
  if (t === null) return null;
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((t - now) / DAY_MS);
}

export function describeDaysLeft(days) {
  if (days === null || days === undefined) return '';
  if (days === 0) return 'today';
  const abs = Math.abs(days);
  const span = abs > 90 ? `${Math.round(abs / 30.44)} months` : `${abs} day${abs === 1 ? '' : 's'}`;
  return days < 0 ? `${span} ago` : `in ${span}`;
}

export function getExpiryInfo({ item, batches, warehouseExpiry, warehouseCode = null, today = new Date() }) {
  const skus = new Set(
    [item?.primary_sku || item?.parent_sku, ...(item?.child_skus || [])]
      .filter(Boolean)
      .map((s) => String(s).toUpperCase())
  );
  const warehouses = (item?.warehouses || []).filter(
    (w) => !warehouseCode || w.warehouse_code === warehouseCode
  );
  const whCodes = new Set(warehouses.map((w) => w.warehouse_code));
  const holds = (warehouseExpiry?.holds || []).filter(
    (h) => skus.has(String(h.primary_sku).toUpperCase()) && whCodes.has(h.warehouse_code)
  );
  const lots = (warehouseExpiry?.lots || []).filter(
    (l) => skus.has(String(l.primary_sku).toUpperCase()) && whCodes.has(l.warehouse_code) && (l.qty || 0) > 0
  );
  const stockBatches = (batches || []).filter(
    (b) => (b.current_quantity || 0) > 0 && whCodes.has(b.warehouse_code)
  );

  // Earliest real expiry across stock still held (SCM batches + warehouse lots + expired holds).
  const dates = [
    ...stockBatches.filter((b) => b.has_expiry !== false).map((b) => b.expiry_date),
    ...lots.map((l) => l.expiry_date),
    ...holds.map((h) => h.expiry_date),
  ].filter((d) => toUtcDay(d) !== null);
  const earliestDate = dates.length
    ? dates.reduce((min, d) => (toUtcDay(d) < toUtcDay(min) ? d : min)).slice(0, 10)
    : null;
  const daysLeft = earliestDate ? daysUntil(earliestDate, today) : null;

  const blocked = [];
  let blockedQty = 0;
  for (const w of warehouses) {
    const onHand = Math.max(0, Number(w.quantity) || 0);
    const whHolds = holds.filter((h) => h.warehouse_code === w.warehouse_code);
    const reported = whHolds.reduce((s, h) => s + (Number(h.qty) || 0), 0);
    const batchExpired = stockBatches
      .filter((b) => b.warehouse_code === w.warehouse_code && b.is_expired)
      .reduce((s, b) => s + (Number(b.current_quantity) || 0), 0);
    const qty = Math.min(Math.max(reported, batchExpired), onHand);
    if (qty > 0) {
      blocked.push({
        warehouse_code: w.warehouse_code,
        qty,
        sources: reported >= batchExpired ? [...new Set(whHolds.map((h) => h.source))] : ['scm_batch'],
      });
      blockedQty += qty;
    }
  }

  let status = 'none';
  if (blockedQty > 0 || (daysLeft !== null && daysLeft < 0)) status = 'expired';
  else if (daysLeft !== null) status = daysLeft <= EXPIRING_SOON_DAYS ? 'soon' : 'ok';

  return { status, earliestDate, daysLeft, blockedQty, blocked };
}
